import React, {
  useEffect,
  useMemo,
  useState,
  useContext,
  useCallback,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { connect } from 'react-redux';
import {
  setIsAreYouStillThereModalOpen,
  setIsSelfServiceTableSelectionModalOpen,
  setSelfServiceTableSelectionModalData,
} from '../../../../redux/modal/modal.actions';
import {
  setCheckout,
  setCart,
  setOrder,
} from '../../../../redux/store/store.actions';
import {
  setFoodAllergensFilter,
  setFoodIngredientsFilter,
  setFoodPreferencesFilter,
} from '../../../../redux/me/me.actions';
import PropTypes from 'prop-types';
import {
  replaceColorInLottie,
  getBrowserLocale,
} from '../../../../utility/util/util.service';
import Lottie from 'lottie-react';
import areyouthere from '../../../../assets/lottie/areyouthere.json';
import { IntlContext } from '../../../../context/intl.context';
import { history } from '../../../../history';

const AreYouStillThereModal = ({
  isOpen,
  store,
  onApprove,
  onCancel,
  onTimeout,

  setIsAreYouStillThereModalOpen,
  setIsSelfServiceTableSelectionModalOpen,
  setCheckout,
  setCart,
  setOrder,
  setFoodAllergensFilter,
  setFoodIngredientsFilter,
  setFoodPreferencesFilter,
  setSelfServiceTableSelectionModalData,
}) => {
  const [countdown, setCountdown] = useState(15);
  const intl = useIntl();
  const { switchLocale } = useContext(IntlContext);

  // Animation placeholder - will be replaced when actual animation is available
  const areYouStillThereAnimation = useMemo(
    () => (
      <Lottie
        style={{ width: '100%', maxWidth: '125px' }}
        animationData={replaceColorInLottie(
          areyouthere,
          store?.primaryColor || '#22442c',
        )}
        loop
      />
    ),
    [store?.primaryColor],
  );

  const closeModal = useCallback(() => {
    setIsAreYouStillThereModalOpen(false);
    // Reset countdown when modal closes
    setCountdown(15);
  }, [setIsAreYouStillThereModalOpen]);

  const resetEverything = useCallback(() => {
    const browserLocale = getBrowserLocale();

    setCheckout(null);
    setCart(null);
    setOrder(null);
    setFoodPreferencesFilter(null);
    setFoodIngredientsFilter(null);
    setFoodAllergensFilter(null);

    switchLocale(browserLocale);

    history.push(`/store/${store?.slug}`);
  }, [
    setCheckout,
    setCart,
    setOrder,
    setFoodPreferencesFilter,
    setFoodIngredientsFilter,
    setFoodAllergensFilter,
    switchLocale,
    store?.slug,
    store?.merchantProfile?.enableSelfServiceTableSelection,
    setIsSelfServiceTableSelectionModalOpen,
  ]);

  const handleApprove = useCallback(() => {
    closeModal();
    if (onApprove) {
      onApprove();
    }
  }, [closeModal, onApprove]);

  const handleCancelOrder = useCallback(() => {
    resetEverything();
    closeModal();
    if (store?.merchantProfile?.enableSelfServiceTableSelection) {
      setIsSelfServiceTableSelectionModalOpen(true);
      setSelfServiceTableSelectionModalData({
        showReducedModal: false,
      });
    }
    if (onCancel) {
      onCancel();
    }
  }, [resetEverything, closeModal, onCancel]);

  const handleTimeout = useCallback(() => {
    resetEverything();
    closeModal();
    if (onTimeout) {
      onTimeout();
    }
    if (store?.merchantProfile?.enableSelfServiceTableSelection) {
      setIsSelfServiceTableSelectionModalOpen(true);
      setSelfServiceTableSelectionModalData({
        showReducedModal: false,
      });
    }
  }, [resetEverything, closeModal, onTimeout]);

  // Handle countdown timer
  useEffect(() => {
    let timer;
    if (isOpen && countdown > 0) {
      timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
    } else if (isOpen && countdown === 0) {
      // Auto timeout after 30 seconds
      handleTimeout();
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [isOpen, countdown, handleTimeout]);

  // Reset countdown when modal opens
  useEffect(() => {
    if (isOpen) {
      setCountdown(15); // Should be 15 seconds, not 30
    }
  }, [isOpen]);

  // Add this useEffect to handle body scroll when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      // Restore body scroll when modal closes
      document.body.style.overflow = 'unset';
    }

    // Cleanup function to restore scroll on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    let timer;
    if (isOpen && countdown > 0) {
      timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
    } else if (isOpen && countdown === 0) {
      handleTimeout();
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [isOpen, countdown, handleTimeout]);

  return (
    <Modal
      isOpen={isOpen}
      className="modal-dialog modal-dialog-centered"
      toggle={false} // Prevent closing by clicking outside or ESC
      backdrop="static"
      keyboard={false}
    >
      <ModalHeader className="bg-transparent px-1 border-0"></ModalHeader>
      <ModalBody className="text-center px-2 pt-0">
        <div className="d-flex justify-content-center mb-1">
          {areYouStillThereAnimation}
        </div>
        <h3 className="mb-1">
          <FormattedMessage id="areYouStillThereTitle" />
        </h3>
        <span className="d-block mb-2">
          <FormattedMessage id="areYouStillThereSubTitle" />
        </span>
        <span className="d-block">
          {intl.formatMessage({ id: 'autoCloseIn' })} {countdown}s
        </span>
      </ModalBody>
      <ModalFooter className="flex-column border-0">
        <Button color="primary" size="lg" block onClick={handleApprove}>
          <FormattedMessage id="areYouStillThereApproveLabel" />
        </Button>
        <div className="text-center w-100">
          <span
            className="d-block px-50 my-50 primary font-small-3"
            onClick={handleCancelOrder}
          >
            <FormattedMessage id="areYouStillThereCancelLabel" />
          </span>
        </div>
      </ModalFooter>
    </Modal>
  );
};

AreYouStillThereModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  store: PropTypes.object,
  onApprove: PropTypes.func,
  onCancel: PropTypes.func,
  onTimeout: PropTypes.func,
  setIsAreYouStillThereModalOpen: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  isOpen: state.modals.isAreYouStillThereModalOpen,
  store: state.store.store,
});

export default connect(mapStateToProps, {
  setIsAreYouStillThereModalOpen,
  setIsSelfServiceTableSelectionModalOpen,
  setCheckout,
  setCart,
  setOrder,
  setFoodAllergensFilter,
  setFoodIngredientsFilter,
  setFoodPreferencesFilter,
  setSelfServiceTableSelectionModalData,
})(AreYouStillThereModal);
